import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { useRequestQueryClient } from "@/lib/queryClient";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { PostHogProvider } from "posthog-js/react";
import { posthogOptions } from "@/lib/posthog";
import { useEffect, useState } from "react";
import { AdminCommandPalette } from "@/components/AdminCommandPalette";
import { ChatProvider } from "@/contexts/ChatContext";
import ChatLauncher from "@/components/Chat/ChatLauncher";
import ChatPanel from "@/components/Chat/ChatPanel";
import * as Sentry from "@sentry/react";
import { usePageContext } from "vike-react/usePageContext";
import { isEmbeddedPreview } from "@/lib/embeddedPreview";
import OnboardingReturnCapture from "@/components/onboarding/OnboardingReturnCapture";
import "@/index.css";

const FRONTEND_DSN = "https://2dd837ad63b2bfc6c98e43fa449c5769@o4510198914940928.ingest.de.sentry.io/4511370324344912";

if (typeof window !== "undefined") {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN || FRONTEND_DSN,
    environment: import.meta.env.MODE,
    sendDefaultPii: true,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0,
  });
}

const GOOGLE_CLIENT_ID =
  typeof window !== "undefined"
    ? (import.meta.env.VITE_GOOGLE_CLIENT_ID || "")
    : "";

// Not on /moderator (the moderator shell mounts its own palette; two Cmd+K listeners would open two
// dialogs) and never inside the moderator preview iframe. Rendered post-mount only (BrowserWidgets),
// so reading isEmbeddedPreview() here cannot cause a hydration mismatch.
function AdminCommandPaletteGate() {
  const { user } = useAuth();
  const pageContext = usePageContext();
  const pathname = pageContext.urlPathname || "";
  if (!user?.isAdmin) return null;
  if (pathname.startsWith("/moderator") || isEmbeddedPreview()) return null;
  return <AdminCommandPalette />;
}

// Browser-only widgets that render DOM (Toaster <section>, chat shell, command
// palette). Deferring them until after mount keeps the first client render
// identical to the server HTML, avoiding a hydration mismatch on SSR pages.
//
// THE CHAT SHELL LIVES INSIDE THIS GATE AND MUST STAY THERE. It is what makes the
// whole panel SSR-safe: the unread dot reads localStorage, the form-factor switch
// reads a media query, and both would otherwise render one value on the server and
// another on the first client pass. There is exactly ONE floating round button on
// any page after Phase 38, and it is ChatLauncher.
function BrowserWidgets() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  if (!mounted) return null;
  return (
    <>
      <AdminCommandPaletteGate />
      {/* Non-visual: POSTs a signed onboarding return token (?ort=) once, signed in or out (49-16). */}
      <OnboardingReturnCapture />
      <Sonner />
      <ChatProvider>
        <ChatLauncher />
        <ChatPanel />
      </ChatProvider>
    </>
  );
}

function Providers({ children }: { children: React.ReactNode }) {
  const queryClient = useRequestQueryClient();
  if (typeof window === "undefined") {
    // Server: minimal providers, no browser-dependent ones
    return (
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          {children}
        </TooltipProvider>
      </QueryClientProvider>
    );
  }

  // Inside the moderator preview iframe PostHog is opted out at init, so every capture path
  // ($pageview, autocapture, replay, usePostHogTracking calls on the framed page) is a no-op and a
  // preview never counts as store traffic. Client-only branch, so no hydration concern.
  const embedded = isEmbeddedPreview();

  return (
    <PostHogProvider
      apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
      options={
        embedded
          ? {
              ...posthogOptions,
              opt_out_capturing_by_default: true,
              capture_pageview: false,
              capture_pageleave: false,
              autocapture: false,
              disable_session_recording: true,
            }
          : posthogOptions
      }
    >
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <TooltipProvider>
              {children}
              <BrowserWidgets />
            </TooltipProvider>
          </AuthProvider>
        </QueryClientProvider>
      </GoogleOAuthProvider>
    </PostHogProvider>
  );
}

export default function Layout({ children }: { children: React.ReactNode }) {
  return <Providers>{children}</Providers>;
}
